/**
 * Product state scripts. Two small, independent pieces:
 *
 * 1. Sticky bar for sold-out and coming-soon products (blocks/sold-out-sticky-bar.liquid).
 *
 * The bar starts hidden. It is shown (data-visible="true") while the Notify me form (blocks/sold-out-actions.liquid,
 * the element with the id named in data-panel) is out of view, and hidden again while the form is on screen. The
 * bottom strip the bar covers is left out of the "in view" test, so a form hidden behind the bar still counts as out
 * of view. The Notify button scrolls to the form and puts the cursor in its email field; without this script it is a
 * plain link to the form. After a successful sign-up (the thank-you message is showing) the bar stays hidden.
 *
 * 2. Release rollover (blocks/product-state.liquid, the "Releases <date>" line).
 *
 * An element with data-release-reload="<unix seconds>" marks a page that was drawn while the product was still coming
 * soon; the time is midnight store time at the start of the release date (the moment the countdown reaches zero). When
 * that moment arrives the page reloads once, so the product shows as released (price and Add to cart instead of Notify
 * me) without the shopper pressing refresh. A page that is opened after that moment but was served from a cached copy
 * reloads once as well. A reload is remembered per tab (sessionStorage), so it can never loop. It waits while someone
 * is typing in a field, and does nothing in the theme editor.
 */

class SoldOutStickyBar extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const panel = this.#panel();
    const notify = this.querySelector('[data-ss-notify]');

    if (!panel) {
      // No Notify me form on this page: the button would lead nowhere, so drop it (and the bar if nothing is left)
      notify?.remove();
      if (!this.querySelector('a, button')) this.remove();
      else this.dataset.visible = 'true';
      return;
    }

    notify?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#goToPanel();
    });

    if (!('IntersectionObserver' in window)) {
      this.dataset.visible = 'true';
      return;
    }

    // The bar is about 64px tall, so the bottom 72px of the screen do not count as "in view"
    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        // Once the thank-you message has replaced the form, the person is on the list: no more nudging
        const signedUp = panel.querySelector('[data-success]:not([hidden])') !== null;
        this.dataset.visible = String(!entry.isIntersecting && !signedUp);
      },
      { rootMargin: '0px 0px -72px 0px' }
    );
    this.#observer.observe(panel);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** @returns {HTMLElement | null} */
  #panel() {
    const id = this.dataset.panel;
    return id ? document.getElementById(id) : null;
  }

  #goToPanel() {
    const panel = this.#panel();
    if (!panel) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

    // The form is replaced by a thank-you message after a request, and then has no field left to focus
    const field = panel.querySelector('input[type="email"]');
    if (field instanceof HTMLElement && !field.closest('[hidden]')) field.focus({ preventScroll: true });
  }
}

if (!customElements.get('sold-out-sticky-bar')) customElements.define('sold-out-sticky-bar', SoldOutStickyBar);

/** The longest delay setTimeout accepts (about 24.8 days); a longer wait comes back and checks again */
const MAX_TIMEOUT = 2147483647;

/**
 * Reloads the page once when the release moment has passed.
 * @param {HTMLElement} marker the element carrying data-release-reload
 */
function watchRelease(marker) {
  const release = Number(marker.dataset.releaseReload);
  if (!release || release <= 0) return;
  // Not while the theme editor is open: the editor draws the page itself
  if (window.Shopify && window.Shopify.designMode) return;

  const key = `ps-release-reload:${location.pathname}:${release}`;

  /** @returns {boolean | null} whether this tab already reloaded for this release; null when storage is unavailable */
  const alreadyReloaded = () => {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return null;
    }
  };

  const reload = () => {
    // Do not pull the page away from someone who is typing (the Notify me email, for example): try again shortly
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      setTimeout(reload, 15000);
      return;
    }
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      // Storage is blocked: still reload, a page opened after the moment never reloads without the remembered flag
    }
    window.location.reload();
  };

  /** @param {boolean} [onLoad] true on the first check, when the page has just been opened */
  const check = (onLoad) => {
    const remaining = release * 1000 - Date.now();
    if (remaining > 0) {
      // A little past the moment, so the server's clock has certainly rolled over too
      setTimeout(check, Math.min(remaining + 1500, MAX_TIMEOUT));
      return;
    }

    const done = alreadyReloaded();
    if (done === true) return;

    if (onLoad === true) {
      // Opened after the moment from a cached copy of the page: one refresh gets the current one
      if (done === false) reload();
      return;
    }

    // The page was open when the moment came: spread the reloads over a few seconds so open pages do not all ask at once
    setTimeout(reload, Math.random() * 3000);
  };

  check(true);
}

const releaseMarker = document.querySelector('[data-release-reload]');
if (releaseMarker instanceof HTMLElement) watchRelease(releaseMarker);